import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

function Quiz({ userId, level }) {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [availableWords, setAvailableWords] = useState([]);

  useEffect(() => {
    const fetchWords = async () => {
      try {
        const { data: allWords, error: allWordsError } = await supabase
          .from('words')
          .select('*')
          .eq('user_id', userId);

        if (allWordsError) throw new Error(allWordsError.message);

        const { data, error } = await supabase
          .from('words')
          .select('*')
          .eq('user_id', userId)
          .not('distractors', 'is', null);

        if (error) throw new Error(error.message);

        const wordsWithDistractors = data.filter(word =>
          word.distractors && word.distractors.length >= 3
        );

        if (wordsWithDistractors.length === 0) {
          if (allWords.length === 0) {
            setError("You haven't added any words yet. Please add some words first.");
          } else {
            setError(`You have ${allWords.length} word${allWords.length === 1 ? '' : 's'} but none have distractors. Add words with meanings to generate distractors automatically.`);
          }
        } else {
          setAvailableWords(wordsWithDistractors);
        }
      } catch (err) {
        setError('Failed to fetch words: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (!userId) { setLoading(false); return; }
    fetchWords();
  }, [userId]);

  useEffect(() => {
    if (!loading && !error && availableWords.length > 0 && !currentQuestion) {
      startQuiz();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, availableWords, currentQuestion]);

  const generateQuestion = () => {
    if (availableWords.length === 0) return;
    const randomWord = availableWords[Math.floor(Math.random() * availableWords.length)];
    const correctAnswer = randomWord.meaning;
    const distractors = randomWord.distractors || [];
    if (distractors.length < 3) return;

    let answerOptions = [correctAnswer, ...distractors.slice(0, 3)];
    for (let i = answerOptions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [answerOptions[i], answerOptions[j]] = [answerOptions[j], answerOptions[i]];
    }

    setCurrentQuestion({ word: randomWord.word, correctAnswer, answerOptions, wordId: randomWord.id });
    setSelectedAnswer(null);
    setShowResult(false);
  };

  const handleAnswerSelect = (answer) => {
    if (showResult) return;
    setSelectedAnswer(answer);
    const correct = answer === currentQuestion.correctAnswer;
    setIsCorrect(correct);
    setShowResult(true);
    if (correct) setScore(prev => prev + 1);
    setQuestionsAnswered(prev => prev + 1);
  };

  const startQuiz = () => {
    setScore(0);
    setQuestionsAnswered(0);
    setError('');
    generateQuestion();
  };

  const nextQuestion = () => generateQuestion();

  const resetQuiz = () => {
    setScore(0);
    setQuestionsAnswered(0);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setShowResult(false);
    setError('');
  };

  const getAnswerClass = (answer) => {
    const base = 'w-full p-4 border rounded-lg text-left text-sm font-medium transition-all relative';
    if (!showResult) {
      if (selectedAnswer === answer) return `${base} border-lattice-green bg-green-50 text-lattice-dark`;
      return `${base} border-lattice-card-border bg-white hover:border-lattice-green cursor-pointer text-lattice-dark`;
    }
    if (answer === currentQuestion.correctAnswer) {
      return `${base} border-emerald-400 bg-emerald-50 text-emerald-900 animate-correct-pulse cursor-default`;
    }
    if (selectedAnswer === answer) {
      return `${base} border-red-300 bg-red-50 text-red-900 animate-incorrect-shake cursor-default`;
    }
    return `${base} border-lattice-card-border bg-white opacity-40 cursor-default text-lattice-dark`;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-lattice-green rounded-full animate-spin" />
        <p className="text-lattice-muted text-sm">Loading quiz...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="p-6 bg-lattice-bg border border-lattice-card-border rounded-xl">
          <h3 className="text-lattice-dark text-lg font-semibold mb-2">Quiz not ready yet</h3>
          <p className="text-lattice-muted text-sm leading-relaxed mb-1">{error}</p>
          <p className="text-lattice-muted text-xs">
            The quiz requires words with AI-generated distractors to create multiple choice questions.
          </p>
        </div>

        <div className="p-5 bg-white border border-lattice-card-border rounded-xl">
          <h4 className="text-lattice-dark font-semibold text-sm mb-3">How to get started:</h4>
          <ol className="text-lattice-muted text-sm space-y-1 pl-5 list-decimal leading-relaxed">
            <li>Go to the Word Input page</li>
            <li>Add a new word with its meaning</li>
            <li>AI will automatically generate distractors</li>
            <li>Come back here to test your knowledge</li>
          </ol>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/word-input')}
            className="px-5 py-2.5 bg-lattice-green hover:bg-lattice-green-hover text-lattice-btn-text rounded-lg text-sm font-medium transition-colors"
          >
            Add words
          </button>
          <button
            onClick={() => navigate('/quiz')}
            className="px-5 py-2.5 border border-lattice-card-border text-lattice-muted hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
          >
            Back to quiz levels
          </button>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="text-center py-10">
        <h2 className="text-lattice-dark text-2xl font-bold mb-8">Quiz Mode</h2>
        <div className="p-8 bg-lattice-bg border border-lattice-card-border rounded-xl max-w-md mx-auto">
          <h3 className="text-lattice-dark font-semibold text-base mb-3">Ready to test your knowledge?</h3>
          <p className="text-lattice-muted text-sm leading-relaxed mb-6">
            You have {availableWords.length} word{availableWords.length === 1 ? '' : 's'} ready for the quiz.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={startQuiz}
              className="px-8 py-3 bg-lattice-green hover:bg-lattice-green-hover text-lattice-btn-text rounded-lg text-base font-semibold transition-colors"
            >
              Start Quiz
            </button>
            <button
              onClick={() => navigate('/quiz')}
              className="px-6 py-2.5 text-lattice-muted hover:text-lattice-dark text-sm transition-colors"
            >
              Back to quiz levels
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Score header */}
      <div className="flex justify-between items-center p-4 bg-lattice-bg border border-lattice-card-border rounded-xl">
        <h2 className="text-lattice-dark text-base font-semibold">Quiz Mode</h2>
        <div className="text-right">
          <div className="text-base font-bold text-lattice-dark">Score: {score}/{questionsAnswered}</div>
          <div className="text-xs text-lattice-muted">Question {questionsAnswered + 1}</div>
        </div>
      </div>

      {/* Question card */}
      <div className="p-6 bg-white border border-lattice-card-border rounded-xl shadow-sm animate-fade-in">
        <h3 className="text-lattice-dark text-xl font-semibold text-center mb-6">
          What does "{currentQuestion.word}" mean?
        </h3>

        <div className="space-y-3">
          {currentQuestion.answerOptions.map((answer, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(answer)}
              disabled={showResult}
              className={getAnswerClass(answer)}
            >
              <span className="font-semibold mr-2 text-gray-400">{String.fromCharCode(65 + index)}.</span>
              {answer}
              {showResult && answer === currentQuestion.correctAnswer && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-500 font-bold">✓</span>
              )}
              {showResult && selectedAnswer === answer && answer !== currentQuestion.correctAnswer && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-red-400 font-bold">✗</span>
              )}
            </button>
          ))}
        </div>

        {/* Result feedback */}
        {showResult && (
          <div className={`mt-5 p-4 rounded-lg text-center border animate-fade-in ${
            isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
          }`}>
            <h4 className={`font-semibold text-base mb-1 ${isCorrect ? 'text-emerald-800' : 'text-red-800'}`}>
              {isCorrect ? 'Correct' : 'Incorrect'}
            </h4>
            <p className={`text-sm ${isCorrect ? 'text-emerald-700' : 'text-red-700'}`}>
              {isCorrect
                ? 'Well done. You earned a point.'
                : `The correct answer was: "${currentQuestion.correctAnswer}"`}
            </p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-center gap-3">
        {showResult ? (
          <>
            <button
              onClick={nextQuestion}
              className="px-6 py-2.5 bg-lattice-green hover:bg-lattice-green-hover text-lattice-btn-text rounded-lg text-sm font-medium transition-colors"
            >
              Next question
            </button>
            <button
              onClick={resetQuiz}
              className="px-6 py-2.5 border border-lattice-card-border text-lattice-muted hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
            >
              Restart
            </button>
          </>
        ) : (
          <button
            onClick={resetQuiz}
            className="px-6 py-2.5 border border-lattice-card-border text-lattice-muted hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
          >
            Reset quiz
          </button>
        )}
      </div>

      {/* Back button */}
      <div className="text-center">
        <button
          onClick={() => navigate('/quiz')}
          className="text-lattice-muted hover:text-lattice-dark text-sm transition-colors"
        >
          Back to quiz levels
        </button>
      </div>
    </div>
  );
}

export default Quiz;
